<template>
  <div class="evthub evthub-app">
    <a class="skip" href="#main">Skip to content</a>

    <EvtHeader
      v-if="event"
      :crumb="crumb"
      :share-title="shareTitle"
      :verified="!!hubProvenance"
      :provenance="hubProvenance"
      :back-to="backTo"
      :back-label="backLabel"
      ghost
    />

    <div v-if="event" class="app" :class="{ mini: collapsed }">
      <HubSidebar
        :event="event"
        :event-id="eventId"
        :dashboard="dashboard"
        :media="media"
        :recordings="recordings"
        :has-provenance="!!hubProvenance"
        :open="navOpen"
        :collapsed="collapsed"
        @close="navOpen = false"
        @toggle="collapsed = !collapsed"
      />

      <button
        class="nav-toggle only-mobile"
        type="button"
        :aria-expanded="navOpen ? 'true' : 'false'"
        @click="navOpen = !navOpen"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
        Contents
      </button>

      <main id="main" class="app-main">
        <router-view
          :event="event"
          :event-id="eventId"
          :dashboard="dashboard"
          :media="media"
          :recordings="recordings"
          :hub-provenance="hubProvenance"
        />
      </main>
    </div>

    <main v-else-if="error" id="main" class="doc">
      <div class="cover">
        <div class="cover-main">
          <p class="kicker">Event brief</p>
          <h1 class="cover-title">This brief could not be loaded</h1>
          <p class="cover-lede">
            The link may be incorrect, or the recording is still being prepared.
          </p>
        </div>
      </div>
    </main>

    <main v-else id="main" class="doc">
      <div class="cover" style="padding-top: 58px">
        <div class="cover-main">
          <div class="skel" style="height: 14px; width: 220px"></div>
          <div
            class="skel"
            style="height: 44px; width: 80%; margin-top: 20px"
          ></div>
          <div
            class="skel"
            style="height: 16px; width: 60%; margin-top: 18px"
          ></div>
        </div>
        <div class="skel" style="aspect-ratio: 16/9; border-radius: 12px"></div>
      </div>
    </main>
  </div>
</template>

<script>
/**
 * The brief's application shell.
 *
 * event.json is fetched once here and handed to every page below, so moving between
 * Insights, Moments and a session is a route change rather than a reload. The shell
 * also owns the masthead and the sidebar - pages render content only.
 */
import { fetchEvent, normalizeProvenance } from './useEvent';
import EvtHeader from './components/EvtHeader.vue';
import HubSidebar from './components/HubSidebar.vue';

export default {
  name: 'EventShell',
  components: { EvtHeader, HubSidebar },
  data() {
    // the rail starts collapsed on a session page; the viewer can open it again
    return {
      data: null,
      error: false,
      navOpen: false,
      collapsed: !!this.$route.params.recId,
    };
  },
  computed: {
    eventId() {
      return this.$route.params.id;
    },
    event() {
      return this.data && this.data.event;
    },
    dashboard() {
      return (this.data && this.data.dashboard) || {};
    },
    media() {
      return (this.data && this.data.media) || {};
    },
    recordings() {
      return (this.data && this.data.recordings) || [];
    },
    activeRec() {
      const id = this.$route.params.recId;
      return id ? this.recordings.find((r) => r.id === id) || null : null;
    },
    hubProvenance() {
      return normalizeProvenance(this.recordings[0] || null);
    },
    // the sidebar and the hero already carry the event title; the masthead only
    // names a session when one is open
    crumb() {
      return this.activeRec ? this.activeRec.title : '';
    },
    // inside a session the masthead offers the way back up; elsewhere the sidebar is it
    backTo() {
      return this.activeRec
        ? { name: 'Event Hub', params: { id: this.eventId } }
        : null;
    },
    backLabel() {
      return this.activeRec ? 'The brief' : '';
    },
    shareTitle() {
      return (
        (this.event && this.event.share && this.event.share.title) ||
        (this.event && this.event.title) ||
        'Decast'
      );
    },
  },
  created() {
    this.load();
  },
  watch: {
    eventId() {
      this.load();
    },
    // entering a session collapses the rail, leaving one opens it
    activeRec(v, old) {
      if (v && !old) this.collapsed = true;
      if (!v && old) this.collapsed = false;
    },
    // A new PAGE starts at the top. Watch the path, not the whole route: the insights
    // filters keep their state in the query string, and scrolling to the top on every
    // keystroke would make the search unusable.
    '$route.path': function onPath() {
      this.navOpen = false;
      if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
    },
  },
  methods: {
    load() {
      this.data = null;
      this.error = false;
      fetchEvent(this.eventId)
        .then((d) => {
          this.data = d;
          if (d && d.event && d.event.title)
            document.title = `${d.event.title} · Decast`;
        })
        .catch(() => {
          this.error = true;
        });
    },
  },
};
</script>

<style lang="scss" src="./styles/event-hub.scss"></style>
